<template>
  <div :class="[{ 'opacity-50': props.disabled}]">
    <!-- Drop area / file picker -->
    <div
      v-if="!modelValue"
      :class="[
        'w-full flex flex-col items-center justify-center bg-white p-8 border-2 border-gray-500 border-dashed rounded-lg ',
        {
          'cursor-not-allowed': props.disabled,
          'cursor-pointer': !props.disabled,
          'hover:border-principal-500': !isDragging && !props.disabled,
          'border-principal-500': isDragging && !props.disabled,
          'border-red-500': props.error && !props.disabled,
        },
      ]"
      @dragover.prevent="onDragOver"
      @dragleave.prevent="onDragLeave"
      @drop="onDrop"
      @click="openFilePicker"
    >
      <input
        ref="fileInput"
        type="file"
        class="hidden"
        :accept="acceptedTypes"
        @change="handleFileChange"
        :disabled="props.disabled"
      />

      <svg
        class="w-15 h-15 sm:w-25 sm:h-25 text-gray-500"
        viewBox="0 0 151 121"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <g clip-path="url(#clip0)">
          <path
            d="M34.6826 113.184C16.0498 113.184 0.932617 98.0664 0.932617 79.4336C0.932617 64.7148 10.3545 52.1992 23.4795 47.582C23.4561 46.9492 23.4326 46.3164 23.4326 45.6836C23.4326 24.9648 40.2139 8.18359 60.9326 8.18359C74.8311 8.18359 86.9482 15.7305 93.4404 26.9805C97.0029 24.5898 101.315 23.1836 105.933 23.1836C118.354 23.1836 128.433 33.2617 128.433 45.6836C128.433 48.543 127.894 51.2617 126.933 53.793C140.62 56.5586 150.933 68.6758 150.933 83.1836C150.933 99.7539 137.503 113.184 120.933 113.184H34.6826ZM53.1982 62.3242C50.9951 64.5273 50.9951 68.0898 53.1982 70.2695C55.4014 72.4492 58.9639 72.4727 61.1436 70.2695L70.2842 61.1289L70.3076 92.5586C70.3076 95.6758 72.8154 98.1836 75.9326 98.1836C79.0498 98.1836 81.5576 95.6758 81.5576 92.5586V61.1289L90.6982 70.2695C92.9014 72.4727 96.4639 72.4727 98.6436 70.2695C100.823 68.0664 100.847 64.5039 98.6436 62.3242L79.8936 43.5742C77.6904 41.3711 74.1279 41.3711 71.9482 43.5742L53.1982 62.3242Z"
            fill="#7B7979"
          />
        </g>
        <defs>
          <clipPath id="clip0">
            <rect
              width="150"
              height="120"
              fill="white"
              transform="translate(0.932617 0.683594)"
            />
          </clipPath>
        </defs>
      </svg>
      <p class="mt-4 text-base text-gray-600 font-semibold text-center">
        Drop your file here or click to upload
      </p>
      <p class="text-sm sm:text-base text-gray-500 text-center">
        Accepted formats: {{ extractExtensionInString(acceptedTypes) }}
      </p>
    </div>

    <div
      v-else
      class="w-full flex flex-col bg-white p-6 border border-gray-300 rounded-lg"
    >
      <div class="flex justify-between items-center">
        <div class="text-lg text-gray-700">
          <span class="font-semibold">{{ modelValue.name }}</span>
          <span class="ml-2">({{ formatFileSize(modelValue.size) }})</span>
        </div>
        <button
          @click="removeFile"
          class="text-red-700 font-bold w-10 h-10 bg-red-300 hover:bg-red-500 cursor-pointer rounded-md disabled:opacity-50 disabled:cursor-not-allowed disabled:bg-red-300"
          :disabled="props.disabled"
        >
          X
        </button>
      </div>
    </div>
    <span v-if="error" class="text-red-500 text-sm">{{ props.error }}</span>
  </div>
</template>

<script setup>
import { ref, defineProps, defineEmits } from "vue";
import { extractExtensionInString } from "@/shared/utils/helper.js";
import { formatFileSize } from "@/shared/utils/helper.js";

const props = defineProps({
  modelValue: {
    type: File,
    default: null,
  },
  error: {
    type: String,
    default: "",
  },
  acceptedTypes: {
    type: String,
    // default: "audio/wav, audio/x-wav, audio/mp3, audio/mpeg",
    default: "audio/wav, audio/mp3",
  },
  disabled: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(["update:modelValue"]);

const fileInput = ref(null);
const isDragging = ref(false);

const onDragOver = () => {
  isDragging.value = true;
};

const onDragLeave = () => {
  isDragging.value = false;
};

const onDrop = (event) => {
  event.preventDefault();
  isDragging.value = false;
  const file = event.dataTransfer.files[0];
  if (isValidFile(file)) {
    emit("update:modelValue", file);
  }
};

const handleFileChange = (event) => {
  const file = event.target.files[0];
  if (isValidFile(file)) {
    emit("update:modelValue", file);
  }
};

const openFilePicker = () => {
  fileInput.value && fileInput.value.click();
};

const removeFile = () => {
  emit("update:modelValue", null);
  if (fileInput.value) {
    fileInput.value.value = "";
  }
};

const isValidFile = (file) => {
  const validTypes = [
    "audio/wav",
    "audio/x-wav",
    "audio/mp3",
    "audio/mpeg",
    "image/png",
    "image/jpeg",
  ];
  if (!file || !validTypes.includes(file.type)) {
    alert("Please upload a valid audio file (WAV or MP3)");
    return false;
  }
  return true;
};


</script>

<style scoped></style>
